import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash, Clock, Hash, Calendar, ToggleLeft, ToggleRight, Check, AlertTriangle } from 'lucide-react';
import { PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../supabaseClient';
import { cn } from '../../lib/utils';
import { createPortal } from 'react-dom';

const WEEK_DAYS = [
  { id: 0, name: 'Sun' },
  { id: 1, name: 'Mon' },
  { id: 2, name: 'Tue' },
  { id: 3, name: 'Wed' },
  { id: 4, name: 'Thu' },
  { id: 5, name: 'Fri' },
  { id: 6, name: 'Sat' },
];

const ConditionRow = ({ condition, variables, onUpdate, onRemove, onEdit }) => {
  const variable = variables.find(v => v.id === condition.variable_id);

  if (!variable) return null;

  const renderValue = () => {
    if (variable.name === 'day_of_week') {
      const isMultiOperator = ['in', 'not_in'].includes(condition.operator);

      if (isMultiOperator) {
        // Handle multiple days for "in" and "not_in"
        const valueArray = Array.isArray(condition.value) ? condition.value : [];
        const dayNames = valueArray.map(dayId => WEEK_DAYS.find(d => d.id === dayId)?.name).join(', ');
        return <span className="font-mono bg-gray-100 px-2 py-1 rounded">{dayNames || 'None'}</span>;
      } else {
        // Handle single day for "is" and "is_not"
        const dayId = condition.value;
        const dayName = WEEK_DAYS.find(d => d.id === dayId)?.name;
        return <span className="font-mono bg-gray-100 px-2 py-1 rounded">{dayName || 'None'}</span>;
      }
    }

    if (variable.name === 'time_of_day') {
      const isRangeOperator = ['between', 'not_between'].includes(condition.operator);

      if (isRangeOperator) {
        // Handle time range for "between" and "not_between"
        const valueArray = Array.isArray(condition.value) ? condition.value : [];
        if (valueArray.length >= 2) {
          return <span className="font-mono bg-gray-100 px-2 py-1 rounded">{valueArray[0]} - {valueArray[1]}</span>;
        }
        return <span className="font-mono bg-gray-100 px-2 py-1 rounded">{valueArray.join(' - ')}</span>;
      } else {
        // Handle single time for "is" and "is_not"
        const timeValue = condition.value;
        return <span className="font-mono bg-gray-100 px-2 py-1 rounded">{timeValue}</span>;
      }
    }

    return <span className="font-mono bg-gray-100 px-2 py-1 rounded">{String(condition.value)}</span>;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
    >
      <div className="flex items-center gap-2 text-sm text-gray-700">
        <span className="font-semibold text-gray-900">{variable.friendly_name}</span>
        <span className="text-gray-500">{condition.operator}</span>
        {renderValue()}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="text-gray-400 hover:text-blue-500 p-1 rounded-full hover:bg-blue-50 transition-colors"
          aria-label="Edit condition"
        >
          <PencilSimple size={16} />
        </button>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
        >
          <Trash size={16} />
        </button>
      </div>
    </motion.div>
  );
};

const AddConditionForm = ({ variables, onAdd, onCancel, initialCondition }) => {
  const [selectedVarId, setSelectedVarId] = useState(initialCondition?.variable_id || '');
  const [selectedOperator, setSelectedOperator] = useState(initialCondition?.operator || '');
  const [value, setValue] = useState(initialCondition?.value || '');

  const selectedVariable = variables.find(v => v.id === selectedVarId);

  // Helper function to get default value based on variable and operator
  const getDefaultValue = (variable, operator) => {
    if (!variable || !operator) return '';

    if (variable.name === 'day_of_week') {
      // For single operators (is, is_not), use single value
      // For multiple operators (in, not_in), use array
      return ['in', 'not_in'].includes(operator) ? [] : 0;
    }

    if (variable.name === 'time_of_day') {
      // For single operators (is, is_not), use single time
      // For range operators (between, not_between), use array
      return ['between', 'not_between'].includes(operator) ? ['09:00', '17:00'] : '09:00';
    }

    return '';
  };

  useEffect(() => {
    if (initialCondition) {
      setSelectedVarId(initialCondition.variable_id);
      setSelectedOperator(initialCondition.operator);
      setValue(initialCondition.value);
    } else if (selectedVariable && selectedOperator) {
      // Set default value when both variable and operator are selected
      setValue(getDefaultValue(selectedVariable, selectedOperator));
    } else if (selectedVariable) {
      // Set default operator when variable is selected
      setSelectedOperator(selectedVariable.operators[0] || '');
    } else {
      setSelectedOperator('');
      setValue('');
    }
  }, [selectedVariable, selectedOperator, initialCondition]);

  const handleAdd = () => {
    // Basic validation
    if (!selectedVariable || !selectedOperator) {
        toast.error("Please select a condition type and operator.");
        return;
    }

    // Validate value based on operator type
    if (selectedVariable.name === 'day_of_week') {
      if (['in', 'not_in'].includes(selectedOperator) && (!Array.isArray(value) || value.length === 0)) {
        toast.error("Please select at least one day.");
        return;
      }
      if (['is', 'is_not'].includes(selectedOperator) && (value === '' || value === null || value === undefined)) {
        toast.error("Please select a day.");
        return;
      }
    }

    if (selectedVariable.name === 'time_of_day') {
      if (['between', 'not_between'].includes(selectedOperator) && (!Array.isArray(value) || value.length !== 2)) {
        toast.error("Please set both start and end times.");
        return;
      }
      if (['is', 'is_not'].includes(selectedOperator) && !value) {
        toast.error("Please set a time.");
        return;
      }
    }

    onAdd({
      variable_id: selectedVarId,
      operator: selectedOperator,
      value: value
    });
  };

  const renderValueInput = () => {
    if (!selectedVariable || !selectedOperator) return null;

    if (selectedVariable.name === 'day_of_week') {
      const isMultiSelect = ['in', 'not_in'].includes(selectedOperator);

      if (isMultiSelect) {
        // Multiple day selection for "in" and "not_in"
        const currentValue = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600">Select days</label>
            <div className="flex gap-1">
              {WEEK_DAYS.map(day => (
                <button
                  key={day.id}
                  onClick={() => {
                    const newValue = currentValue.includes(day.id)
                      ? currentValue.filter(d => d !== day.id)
                      : [...currentValue, day.id];
                    setValue(newValue.sort());
                  }}
                  className={cn(
                    "flex-1 py-2 text-xs rounded-md border transition-colors",
                    currentValue.includes(day.id) 
                      ? "bg-black text-white border-black" 
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                >
                  {day.name}
                </button>
              ))}
            </div>
          </div>
        );
      } else {
        // Single day selection for "is" and "is_not"
        return (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600">Select day</label>
            <div className="flex gap-1">
              {WEEK_DAYS.map(day => (
                <button
                  key={day.id}
                  onClick={() => setValue(day.id)}
                  className={cn(
                    "flex-1 py-2 text-xs rounded-md border transition-colors",
                    value === day.id
                      ? "bg-black text-white border-black" 
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                >
                  {day.name}
                </button>
              ))}
            </div>
          </div>
        );
      }
    }

    if (selectedVariable.name === 'time_of_day') {
      const isRange = ['between', 'not_between'].includes(selectedOperator);

      if (isRange) {
        // Time range for "between" and "not_between"
        const timeArray = Array.isArray(value) ? value : ['09:00', '17:00'];
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">From</label>
                <input 
                  type="time" 
                  value={timeArray[0]}
                  onChange={e => setValue([e.target.value, timeArray[1]])}
                  className="w-full border border-gray-200 rounded-xl h-10 px-3 text-sm 
                    focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent
                    bg-white"
                />
              </div>
              <div className="flex items-center justify-center pt-6">
                <span className="text-xs font-medium text-gray-400">to</span>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Until</label>
                <input 
                  type="time" 
                  value={timeArray[1]}
                  onChange={e => setValue([timeArray[0], e.target.value])}
                  className="w-full border border-gray-200 rounded-xl h-10 px-3 text-sm 
                    focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent
                    bg-white"
                />
              </div>
            </div>
            <div className="text-center">
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-50 text-xs text-gray-600">
                <Clock size={12} className="mr-1.5" />
                {timeArray[0]} - {timeArray[1]}
              </span>
            </div>
          </div>
        );
      } else {
        // Single time for "is" and "is_not"
        return (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600">Select time</label>
            <input 
              type="time" 
              value={value || '09:00'}
              onChange={e => setValue(e.target.value)}
              className="w-full border border-gray-200 rounded-xl h-10 px-3 text-sm 
                focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent
                bg-white"
            />
          </div>
        );
      }
    }

    return (
      <input 
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        className="w-full border-gray-200 rounded-md text-sm"
        placeholder="Enter value"
      />
    );
  };

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-dashed space-y-3">
        <div className="grid grid-cols-2 gap-3">
            <select 
                value={selectedVarId}
                onChange={e => setSelectedVarId(e.target.value)}
                className="w-full border-gray-200 rounded-md text-sm"
                disabled={!!initialCondition}
            >
                <option value="">Select Condition Type...</option>
                {variables.map(v => <option key={v.id} value={v.id}>{v.friendly_name}</option>)}
            </select>
            {selectedVariable && (
                <select
                    value={selectedOperator}
                    onChange={e => setSelectedOperator(e.target.value)}
                    className="w-full border-gray-200 rounded-md text-sm"
                    disabled={!!initialCondition}
                >
                    {selectedVariable.operators.map(op => <option key={op} value={op}>{op}</option>)}
                </select>
            )}
        </div>
        {selectedVariable && renderValueInput()}
        <div className="flex justify-end gap-2 pt-2">
            <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50">Cancel</button>
            <button onClick={handleAdd} className="px-3 py-1.5 text-sm text-white bg-black rounded-md">
              {initialCondition ? 'Update' : 'Add'}
            </button>
        </div>
    </div>
  )
};

const ConditionGroupUI = ({ group, groupIndex, variables, onConditionAdd, onConditionRemove, onConditionEdit, onGroupRemove, isOnlyGroup }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [editingCondition, setEditingCondition] = useState(null);

    const handleAdd = (newCondition) => {
        if (editingCondition) {
            onConditionEdit(group.id, editingCondition.id, newCondition);
            setEditingCondition(null);
        } else {
            onConditionAdd(group.id, newCondition);
        }
        setIsAdding(false);
    }

    const handleEdit = (condition) => {
        setEditingCondition(condition);
        setIsAdding(true);
    }

    const handleCancelEdit = () => {
        setEditingCondition(null);
        setIsAdding(false);
    }

    return (
        <motion.div 
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-4 bg-white rounded-lg border border-gray-200/80 shadow-sm space-y-3 relative"
        >
            <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 bg-gray-100/80 rounded-md px-2 py-1">
                        Group {groupIndex + 1}
                    </span>
                    <span className="text-xs font-medium text-gray-500">All conditions in this group must be true (AND)</span>
                </div>
                {!isOnlyGroup && (
                    <button 
                        onClick={() => onGroupRemove(group.id)} 
                        className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50/50 transition-colors"
                        aria-label="Remove condition group"
                    >
                        <Trash size={16} />
                    </button>
                )}
            </div>
            <div className="space-y-2">
                <AnimatePresence>
                    {group.conditions.map(c => (
                        <ConditionRow
                            key={c.id}
                            condition={c}
                            variables={variables}
                            onRemove={() => onConditionRemove(group.id, c.id)}
                            onEdit={() => handleEdit(c)}
                        />
                    ))}
                </AnimatePresence>
                 {group.conditions.length === 0 && !isAdding && (
                    <div className="text-center py-4 text-sm text-gray-500">
                        This group has no conditions yet.
                    </div>
                )}
            </div>
            {isAdding ? (
                <AddConditionForm 
                    variables={variables}
                    onAdd={handleAdd}
                    onCancel={handleCancelEdit}
                    initialCondition={editingCondition}
                />
            ) : (
                <button 
                    onClick={() => setIsAdding(true)}
                    className="w-full flex items-center justify-center gap-2 py-2 px-4 text-sm text-gray-600 bg-gray-50/80 hover:bg-gray-100 border border-dashed rounded-md transition-colors"
                >
                    <Plus size={16} /> Add Condition
                </button>
            )}
        </motion.div>
    )
}


const ActivationConditionsPanel = ({ setting, onClose, isOpen }) => {
  const supabase = getSupabaseClient();
  const [conditionGroups, setConditionGroups] = useState([]);
  const [environmentalVariables, setEnvironmentalVariables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose(false);
      }
    };

    if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchData = async () => {
      if (!setting?.id) return;
      setIsLoading(true);

      try {
        // Only fetch conditions for real database IDs (not temporary IDs)
        const isRealDatabaseId = !setting.id.toString().startsWith('new-') && 
                                 !setting.id.toString().startsWith('edit-') && 
                                 !setting.id.toString().startsWith('add-');

        const variablesRes = await supabase.from('brain_environmental_variables').select('*');
        if (variablesRes.error) throw variablesRes.error;
        setEnvironmentalVariables(variablesRes.data || []);

        let conditionsData = [];
        if (isRealDatabaseId) {
          const conditionsRes = await supabase.from('brain_settings_conditions').select('*').eq('settings_id', setting.id);
          if (conditionsRes.error) throw conditionsRes.error;
          conditionsData = conditionsRes.data || [];
        }

        const groups = (conditionsData || []).reduce((acc, condition) => {
          const groupId = condition.group_identifier || 'default-group';
          if (!acc[groupId]) {
            acc[groupId] = {
              id: groupId,
              conditions: [],
            };
          }
          acc[groupId].conditions.push(condition);
          return acc;
        }, {});

        let groupedConditions = Object.values(groups);

        if (groupedConditions.length === 0) {
            groupedConditions = [{ id: crypto.randomUUID(), conditions: [] }];
        } else {
            groupedConditions.forEach(group => {
                if (group.id === 'default-group') {
                    group.id = crypto.randomUUID();
                }
            });
        }

        setConditionGroups(groupedConditions);
      } catch (error) {
        console.error("Error fetching conditions data:", error);
        toast.error("Failed to load conditions data.");
      } finally {
        setIsLoading(false);
      }
    };
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, setting, supabase]);

  const handleAddCondition = (groupId, newCondition) => {
    setConditionGroups(prev => prev.map(group => {
        if (group.id === groupId) {
            return {
                ...group,
                conditions: [...group.conditions, { ...newCondition, id: `temp-${Date.now()}` }]
            };
        }
        return group;
    }));
  };

  const handleRemoveCondition = (groupId, conditionId) => {
    setConditionGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.filter(c => c.id !== conditionId)
        };
      }
      return group;
    }));
  };

  const handleEditCondition = (groupId, conditionId, updatedCondition) => {
    setConditionGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.map(c => 
            c.id === conditionId 
              ? { ...c, ...updatedCondition }
              : c
          )
        };
      }
      return group;
    }));
  };

  const handleAddGroup = () => {
    const newGroupId = crypto.randomUUID();
    setConditionGroups(prev => [...prev, { id: newGroupId, conditions: [] }]);
  };

  const handleRemoveGroup = (groupId) => {
    setConditionGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const handleSave = async () => {
    if (!setting?.id) return;

    // Check if this is a new setting (temporary ID)
    const isNewSetting = setting.id.toString().startsWith('new-') || 
                         setting.id.toString().startsWith('edit-') || 
                         setting.id.toString().startsWith('add-');

    if (isNewSetting) {
      toast.info("Please save the setting first, then configure activation conditions.");
      onClose(false);
      return;
    }

    setIsSaving(true);
    try {
      // First, delete all existing conditions for this setting
      const { error: deleteError } = await supabase
        .from('brain_settings_conditions')
        .delete()
        .eq('settings_id', setting.id);

      if (deleteError) throw deleteError;

      // Then, insert all the new conditions from state
      const conditionsToInsert = conditionGroups.flatMap(group => 
        group.conditions.map(({ id, ...rest }) => ({
          ...rest,
          settings_id: setting.id,
          group_identifier: group.id
        }))
      );

      if (conditionsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('brain_settings_conditions')
          .insert(conditionsToInsert);

        if (insertError) throw insertError;
      }

      toast.success("Activation conditions saved!");
      onClose(true); // Pass true to indicate a save happened
    } catch (error) {
      console.error("Error saving conditions:", error);
      toast.error("Failed to save conditions.");
    } finally {
      setIsSaving(false);
    }
  };


  const renderContent = () => {
    if (isLoading) {
      return <div className="p-6 text-center text-gray-500">Loading conditions...</div>;
    }
    return (
      <div className="p-5 sm:p-7 space-y-4 sm:space-y-5 flex-1 overflow-y-auto">
        <div className="space-y-4">
            <AnimatePresence>
                {conditionGroups.map((group, index) => (
                    <motion.div 
                        key={group.id} 
                        className="space-y-4"
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <ConditionGroupUI
                            group={group}
                            groupIndex={index}
                            variables={environmentalVariables}
                            onConditionAdd={handleAddCondition}
                            onConditionRemove={handleRemoveCondition}
                            onConditionEdit={handleEditCondition}
                            onGroupRemove={handleRemoveGroup}
                            isOnlyGroup={conditionGroups.length === 1}
                        />
                        {index < conditionGroups.length - 1 && (
                            <div className="flex justify-center items-center">
                                <span className="text-xs font-bold text-gray-400/80">OR</span>
                            </div>
                        )}
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>

        <button 
            onClick={handleAddGroup}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-medium text-gray-600 bg-gray-100/70 hover:bg-gray-200/70 border border-dashed border-gray-300 rounded-lg transition-colors"
        >
            <Plus size={16} /> Add OR condition group
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={() => onClose(false)}
      >
        <motion.div
          ref={popupRef}
          variants={popupVariants}
          className="bg-gray-50 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white/80 backdrop-blur-sm z-10">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                <Clock size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
              </div>
              <div>
                <h2 className="font-medium text-base sm:text-lg text-gray-900">Activation Conditions</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                  Set rules for when this setting should be active.
                </p>
              </div>
            </div>
            <button
              onClick={() => onClose(false)}
              className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={16} className="sm:w-[18px] sm:h-[18px]" />
            </button>
          </div>

          {renderContent()}

          {/* Footer */}
          <div className="px-5 sm:px-7 py-4 bg-gray-50/80 backdrop-blur-sm flex justify-end gap-2 sm:gap-3 sticky bottom-0 border-t">
            <button
              onClick={() => onClose(false)}
              className="py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-xs sm:text-sm font-medium bg-white hover:bg-gray-50 transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className="py-2 sm:py-2.5 px-4 sm:px-5 rounded-xl bg-black text-white text-xs sm:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : "Save Conditions"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default ActivationConditionsPanel; 